import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, PenLine, Eye, Settings, Sparkles } from 'lucide-react';
import { services } from '../../data/services';

interface ServiceFinderModalProps {
  onClose: () => void;
}

const scenarios = [
  {
    id: 'have-content',
    icon: PenLine,
    label: 'I have content but no website',
    heading: 'You have text, images, maybe even a brand. But nowhere to put it all.',
    description:
      'You need someone to take what you already have and build it into a real, working website. Fast, responsive, professional.',
    serviceIds: ['website-development'],
  },
  {
    id: 'have-site-not-working',
    icon: Eye,
    label: 'My website is not performing',
    heading:
      'Your website exists. But it is not bringing in visitors, or it looks like it was built five years ago.',
    description:
      'Before rebuilding anything, you need to understand what is actually wrong. A focused audit tells you exactly where the problem is and what to fix first.',
    serviceIds: ['design-audit', 'seo-audit', 'content-writing'],
  },
  {
    id: 'done-everything',
    icon: Settings,
    label: 'Everything looks right but nothing works',
    heading:
      'You have a decent looking site. The content is there. The design seems fine. Yet no one is finding you.',
    description:
      'When the surface looks good but results are missing, the problem is usually under the hood. Technical SEO, content structure, or how your pages talk to search engines.',
    serviceIds: ['seo-audit', 'content-writing', 'design-audit'],
  },
  {
    id: 'want-unique',
    icon: Sparkles,
    label: 'I want something built from scratch',
    heading:
      'You do not want a template. You do not want something generic. You want a website that actually represents you.',
    description:
      'Start with homepage prototypes to see real designs before committing. Or go all in with a full build where we handle strategy, content, design, and development. For complex applications, explore our architecture build.',
    serviceIds: ['direction-sprint', 'full-creation', 'architecture-build'],
  },
];

export default function ServiceFinderModal({ onClose }: ServiceFinderModalProps) {
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const activeScenario = scenarios.find((s) => s.id === selected);
  const matchedServices = activeScenario
    ? activeScenario.serviceIds.map((id) => services.find((s) => s.id === id)).filter(Boolean)
    : [];

  return (
    <div className="fixed inset-0 z-[999] flex items-start justify-center">
      <div
        className="absolute inset-0 bg-night-950/80 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      <div className="relative w-full max-w-4xl mx-auto my-4 sm:my-8 max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-4rem)] flex flex-col bg-cream-100 rounded-2xl shadow-2xl animate-scale-in overflow-hidden">
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-night-100 flex-shrink-0">
          <h2 className="text-sm font-bold uppercase tracking-widest text-night-950">
            Let us help you decide
          </h2>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full border-2 border-night-200 text-night-500 hover:bg-night-950 hover:text-cream-100 hover:border-night-950 transition-all duration-300"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-8">
          <div className="mb-8">
            <h3 className="text-display-sm font-black uppercase text-night-950 mb-3">
              Where are you <span className="text-volt-600">right now</span>?
            </h3>
            <p className="text-night-500 max-w-lg">
              Pick the situation that sounds most like yours. We will show you which services make
              sense.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {scenarios.map((scenario) => {
              const Icon = scenario.icon;
              const isActive = selected === scenario.id;
              return (
                <button
                  key={scenario.id}
                  onClick={() => setSelected(isActive ? null : scenario.id)}
                  className={`text-left p-5 sm:p-6 rounded-xl border-2 transition-all duration-300 group ${
                    isActive
                      ? 'border-night-950 bg-night-950 shadow-lg'
                      : 'border-night-200 bg-white hover:border-night-400'
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center mb-4 transition-colors duration-300 ${
                      isActive
                        ? 'bg-volt-500 text-night-950'
                        : 'bg-night-100 text-night-500 group-hover:bg-night-200'
                    }`}
                  >
                    <Icon size={18} />
                  </div>
                  <p
                    className={`text-sm font-bold leading-snug transition-colors duration-300 ${
                      isActive ? 'text-cream-100' : 'text-night-900'
                    }`}
                  >
                    {scenario.label}
                  </p>
                </button>
              );
            })}
          </div>

          {activeScenario && (
            <div className="animate-fade-in">
              <div className="border-t-2 border-night-200 pt-8 mb-6">
                <p className="text-lg font-bold text-night-900 leading-snug mb-2">
                  {activeScenario.heading}
                </p>
                <p className="text-sm text-night-500 leading-relaxed max-w-2xl">
                  {activeScenario.description}
                </p>
              </div>

              <div className="space-y-3 mb-8">
                {matchedServices.map((service) =>
                  service ? (
                    <Link
                      key={service.id}
                      to={`/services/${service.slug}`}
                      onClick={onClose}
                      className="flex items-center justify-between p-5 rounded-xl border-2 border-night-200 bg-white hover:border-night-900 hover:shadow-soft transition-all duration-300 group"
                    >
                      <div className="flex-1 min-w-0 mr-4">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="text-sm font-bold text-night-950 group-hover:text-night-900 truncate">
                            {service.title}
                          </h4>
                          <span className="flex-shrink-0 text-xs font-bold text-volt-600 bg-volt-500/10 px-2.5 py-0.5 rounded-full">
                            {service.price}
                          </span>
                        </div>
                        <p className="text-xs text-night-500 truncate">{service.tagline}</p>
                      </div>
                      <span className="flex-shrink-0 text-xs font-bold uppercase tracking-wider text-night-400 group-hover:text-night-900 transition-colors duration-300">
                        Explore
                      </span>
                    </Link>
                  ) : null,
                )}
              </div>

              <Link to="/services#compare" onClick={onClose} className="btn-secondary text-xs">
                Compare all services
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
